import { capitalize, fmtNum, inPeriod, pluralWord } from "~/lib/format";

// Headlines and deks for the reporters pages, templated from the numbers.
// No model-written text.

/** Share of a total, rounded, safe for a zero total. */
export function share(part: number, total: number): number {
  return total > 0 ? Math.round((part / total) * 100) : 0;
}

/** Smallest number of rows, largest first, that reach half of the total. */
export function halfCover(values: number[]): { n: number; share: number; total: number } {
  const sorted = [...values].filter((v) => v > 0).sort((a, b) => b - a);
  const total = sorted.reduce((s, v) => s + v, 0);
  if (total === 0) return { n: 0, share: 0, total: 0 };
  let acc = 0;
  for (let i = 0; i < sorted.length; i++) {
    acc += sorted[i]!;
    if (acc * 2 >= total) return { n: i + 1, share: share(acc, total), total };
  }
  return { n: sorted.length, share: 100, total };
}

export interface ReportersStoryInput {
  periodLabel: string;
  reporters: number;
  tickets: number;
  /** Resolved in the period, whenever they were created. */
  resolved: number;
  pointsCoverage: number;
  descriptionRate: number;
  createdByReporter: Array<{ name: string; count: number }>;
  byType: Array<{ type: string; count: number; percentage: number }>;
}

export function buildReportersHeadline(s: ReportersStoryInput): string {
  const when = inPeriod(s.periodLabel);
  if (s.tickets === 0) return `No JIRA tickets filed ${when}`;
  return `${fmtNum(s.reporters)} ${pluralWord(s.reporters, "person", "people")} filed ${fmtNum(s.tickets)} ${pluralWord(s.tickets, "ticket")} ${when}, and ${fmtNum(s.resolved)} were resolved`;
}

export function buildReportersDek(s: ReportersStoryInput): string {
  if (s.tickets === 0) return "Nothing to count yet for this period.";
  const parts: string[] = [];
  const cover = halfCover(s.createdByReporter.map((r) => r.count));
  const top = [...s.createdByReporter].sort((a, b) => b.count - a.count)[0];
  if (top && top.count > 0) {
    parts.push(
      `The busiest reporter, ${top.name}, filed ${fmtNum(top.count)} (${share(top.count, s.tickets)}%).`,
    );
  }
  if (cover.n > 0 && s.reporters > 1) {
    parts.push(
      `${capitalize(fmtNum(cover.n))} of ${fmtNum(s.reporters)} filed half of all tickets.`,
    );
  }
  const bug = s.byType.find((t) => t.type.toLowerCase() === "bug");
  if (bug && bug.count > 0) parts.push(`Bugs were ${bug.percentage}% of new tickets.`);
  parts.push(
    `${s.pointsCoverage}% of new tickets carry story points and ${s.descriptionRate}% have a real description.`,
  );
  return parts.join(" ");
}

export interface ReporterStoryInput {
  name: string;
  periodLabel: string;
  created: number;
  resolved: number;
  completionRate: number;
  pointsRate: number;
  descriptionRate: number;
  avgQuality: number;
  topProject: { key: string; name: string; count: number } | null;
}

export function buildReporterHeadline(s: ReporterStoryInput): string {
  const when = inPeriod(s.periodLabel);
  if (s.created === 0) return `${s.name} filed no tickets ${when}`;
  return `${s.name} filed ${fmtNum(s.created)} ${pluralWord(s.created, "ticket")} ${when}; ${fmtNum(s.resolved)} ${s.resolved === 1 ? "is" : "are"} resolved`;
}

export function buildReporterDek(s: ReporterStoryInput): string {
  if (s.created === 0) return "Nothing to count yet for this period.";
  const parts = [
    `${s.pointsRate}% carry story points and ${s.descriptionRate}% have a real description, for an average quality of ${s.avgQuality.toFixed(1)} out of 5.`,
  ];
  if (s.topProject && s.topProject.count > 0) {
    parts.push(
      `Most went to ${s.topProject.name} (${s.topProject.key}), ${fmtNum(s.topProject.count)} of ${fmtNum(s.created)}.`,
    );
  }
  return parts.join(" ");
}
